import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, FileAudio, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { useProfile } from "@/context/ProfileContext";
import { errorMessage } from "@/lib/api";
import { Field, GenreSelect } from "./FormBits";

const MAX_AUDIO = 50 * 1024 * 1024;

export const FilePick = ({ testId, accept, icon: Icon, label, file, preview, onPick }) => (
  <label className="flex cursor-pointer items-center gap-3 rounded-2xl border border-dashed border-white/20 bg-[#0A0B10] p-4 transition-colors hover:border-nw-red/60">
    {preview ? <img src={preview} alt="" className="h-14 w-14 rounded-lg object-cover" /> : <span className="grid h-14 w-14 place-items-center rounded-lg bg-nw-surface"><Icon className="h-6 w-6 text-nw-red" /></span>}
    <span className="min-w-0 text-sm">
      <span className="block font-semibold text-white">{label}</span>
      <span className="block truncate text-xs text-slate-500">{file ? file.name : "Tap to choose a file"}</span>
    </span>
    <input data-testid={testId} type="file" accept={accept} hidden onChange={(e) => onPick(e.target.files?.[0] || null)} />
  </label>
);

export const UploadForm = () => {
  const { profile, request, refresh } = useProfile();
  const navigate = useNavigate();
  const [form, setForm] = useState({ title: "", artist_name: profile.display_name, genre: "", description: "" });
  const [audio, setAudio] = useState(null);
  const [cover, setCover] = useState(null);
  const [progress, setProgress] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  useEffect(() => {
    if (!cover) return setCoverPreview(null);
    const url = URL.createObjectURL(cover);
    setCoverPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [cover]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const pickAudio = (f) => {
    if (f && !/\.(mp3|wav)$/i.test(f.name)) return toast.error("Choose an MP3 or WAV file");
    if (f && f.size > MAX_AUDIO) return toast.error("Music file must be under 50 MB");
    setAudio(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!audio || !cover) return toast.error("Add both a music file and cover artwork");
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    fd.append("audio", audio); fd.append("cover", cover);
    setProgress(0);
    try {
      const t = await request("post", "/tracks", fd, { onUploadProgress: (p) => setProgress(Math.round((p.loaded / (p.total || 1)) * 100)) });
      toast.success("Track published");
      refresh();
      navigate(`/track/${t.id}`);
    } catch (err) { toast.error(errorMessage(err)); setProgress(null); }
  };

  const busy = progress !== null;
  return (
    <form data-testid="upload-form" onSubmit={submit} className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <FilePick testId="upload-audio-input" accept=".mp3,.wav,audio/mpeg,audio/wav" icon={FileAudio} label="Music file (MP3 / WAV)" file={audio} onPick={pickAudio} />
        <FilePick testId="upload-cover-input" accept="image/jpeg,image/png,image/webp" icon={ImagePlus} label="Cover artwork" file={cover} preview={coverPreview} onPick={setCover} />
      </div>
      <Field label="Song title"><input data-testid="upload-title-input" required maxLength={100} value={form.title} onChange={set("title")} className="nw-input" placeholder="Midnight Signal" /></Field>
      <Field label="Artist name"><input data-testid="upload-artist-input" required maxLength={80} value={form.artist_name} onChange={set("artist_name")} className="nw-input" /></Field>
      <Field label="Genre"><GenreSelect testId="upload-genre-select" value={form.genre} onChange={set("genre")} /></Field>
      <Field label="Description"><textarea data-testid="upload-description-input" rows={3} maxLength={1000} value={form.description} onChange={set("description")} className="nw-input resize-none" placeholder="The story, the tools, the vibe..." /></Field>
      <button data-testid="upload-submit-button" disabled={busy} className="btn-red relative w-full overflow-hidden py-3.5 text-sm uppercase tracking-[0.2em] disabled:opacity-80">
        {busy && <span className="absolute inset-y-0 left-0 bg-white/20 transition-[width]" style={{ width: `${progress}%` }} />}
        <span className="relative">{busy ? <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Uploading {progress}%</span> : "Publish track"}</span>
      </button>
    </form>
  );
};
